import React, { FC } from 'react';
import FileSourcePlusButtonPicker from 'app/components/fileSourceInput/FileSourcePlusButtonPicker';
import { useFileSource } from 'app/components/fileSourceInput/hooks/useFileSource';
import DropZone from './fileSourceInput/dropZone';
import { DropdownMenuContentProps } from '@kontentino/ui';
import MediaLibraryPicker from 'app/modules/mediaLibraryPage/components/picker/MediaLibraryPicker';
import MediaLibraryModal from 'app/modules/mediaLibrary/MediaLibraryModal';
import { useIsMediaLibraryEnabled } from 'app/hooks/useIsMediaLibraryEnabled';
import { assetToMediaLibraryItem } from 'app/modules/mediaLibraryPage/utils/assetToMediaLibraryItem';

type Props = {
  variant: 'picker' | 'plus-button' | 'dropzone';
  disabled?: boolean;
  fileSourceController: ReturnType<typeof useFileSource>;
  dropzoneContentProps?: DropdownMenuContentProps;
  classNames?: {
    button?: string;
  };
  triggerButton?: React.ReactNode;
};

const FileSourceInput: FC<Props> = ({
  fileSourceController,
  variant,
  disabled,
  dropzoneContentProps,
  classNames,
  triggerButton,
}) => {
  const { mediaUploader, mediaLibrary, fileSources, params } =
    fileSourceController;
  const { page, maxFiles, allowedFiles, onChange } = params;
  const inputProps = mediaUploader.dropzone.getInputProps();
  const isMediaLibraryEnabled = useIsMediaLibraryEnabled();

  return (
    <>
      {isMediaLibraryEnabled ? (
        <MediaLibraryPicker
          open={mediaLibrary.isShown}
          pageId={page.id}
          maxFiles={maxFiles}
          allowedFiles={allowedFiles}
          onClose={mediaLibrary.hide}
          onConfirm={(assets) => {
            onChange(assets.map(assetToMediaLibraryItem));
            mediaLibrary.hide();
          }}
        />
      ) : (
        <MediaLibraryModal
          limits={{
            selectCount: maxFiles,
            allowedFiles,
          }}
          page={page}
          isShown={mediaLibrary.isShown}
          onHide={mediaLibrary.hide}
          onSelectSubmit={onChange}
        />
      )}
      {variant === 'picker' && (
        <DropZone
          {...mediaUploader.dropzone.getRootProps()}
          isDragActive={mediaUploader.dropzone.isDragActive}
          specifications={{
            accept: inputProps.accept,
            multiple: inputProps.multiple,
          }}
          fileSources={fileSources}
          disabled={disabled}
          removeDefaultClassName
        >
          <DropZone.DropZoneFilePicker
            dropzoneContentProps={dropzoneContentProps}
          />
          <DropZone.DropZoneActiveDragOverlay />
        </DropZone>
      )}
      {variant === 'plus-button' && (
        <FileSourcePlusButtonPicker
          fileSources={fileSources}
          disabled={disabled}
          classNames={classNames}
          dropzoneContentProps={dropzoneContentProps}
          triggerButton={triggerButton}
        />
      )}
      {variant === 'dropzone' && (
        <DropZone
          {...mediaUploader.dropzone.getRootProps()}
          isDragActive={mediaUploader.dropzone.isDragActive}
          specifications={{
            accept: inputProps.accept,
            multiple: inputProps.multiple,
          }}
          fileSources={fileSources}
          disabled={disabled}
        >
          <DropZone.DropZoneHelperText />
          <DropZone.DropZoneFileSelect />
          <DropZone.DropZoneActiveDragOverlay />
        </DropZone>
      )}
      <input {...inputProps} data-cy="file-source_input" disabled={disabled} />
    </>
  );
};

export default FileSourceInput;
